import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { noop } from 'lodash';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pluck, startWith } from 'rxjs/operators';
import { SelectComboboxItem } from './select-combobox-item.model';

@Component({
    selector: 'app-select-combobox',
    templateUrl: './select-combobox.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComboboxComponent),
            multi: true,
        },
    ],
})
export class SelectComboboxComponent implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
    @Input() placeholder: string;
    @Input() disableToggling = false;
    @Input() multiple = false;

    @Input() items: SelectComboboxItem[];
    @Input() createItem?: SelectComboboxItem;
    @Output() addNewClick = new EventEmitter<SelectComboboxItem>();

    readonly filterTextCtrl = new FormControl();

    isOpened = false;
    isDisabled = false;

    onChanged: (selectedItems: any) => void = noop;
    onTouched: () => void = noop;

    private readonly _childItems$ = new BehaviorSubject<SelectComboboxItem[]>([]);
    private readonly _selectedValue$ = new BehaviorSubject<{ emitEvent: boolean; value: any }>({ emitEvent: false, value: undefined });

    private readonly _filterText$ = this.filterTextCtrl.valueChanges.pipe(
        startWith(<string>undefined),
        map(filterText => filterText?.trim()?.toLowerCase() || undefined),
        debounceTime(200),
        distinctUntilChanged(),
    );

    // eslint-disable-next-line @typescript-eslint/member-ordering
    readonly selectedValue$ = this._selectedValue$.pipe(
        pluck('value'),
        distinctUntilChanged(),
    );

    // eslint-disable-next-line @typescript-eslint/member-ordering
    readonly selectedItem$ = combineLatest([this._childItems$, this.selectedValue$]).pipe(
        map(([childItems, selectedValue]) => {
            if (!this.multiple) {
                return selectedValue && childItems.find(item => item.value === selectedValue);
            } else {
                return selectedValue?.length ? childItems.filter(item => selectedValue.includes(item.value)) : [];
            }
        }),
        distinctUntilChanged(),
    );

    // eslint-disable-next-line @typescript-eslint/member-ordering
    readonly displayedItems$ = combineLatest([this._childItems$, this._filterText$, this.selectedItem$]).pipe(
        map(([childItems, filterText, selectedItem]) => {
            const filteredItems = filterText
                ? childItems.filter(item => item.label && item.label.toLowerCase().includes(filterText))
                : childItems;

            return filteredItems.map(item => ({ ...item, isSelected: this.isItemSelected(selectedItem, item) }));
        })
    );

    readonly itemsTrackBy = (item: SelectComboboxItem) => item.value;

    ngOnInit() {
        this._selectedValue$
            .pipe(filter(({ emitEvent }) => emitEvent))
            .subscribe(({ value }) => {
                if (!this.multiple) {
                    this.closeDropdown();
                }
                this.onTouched();
                this.onChanged(value);
            });
    }

    ngOnChanges({ items }: SimpleChanges): void {
        if (items) {
            this._childItems$.next(this.items || []);
        }
    }

    ngOnDestroy(): void {
        this._childItems$.complete();
        this._selectedValue$.complete();
    }

    addNewItem() {
        this.addNewClick.emit(this.createItem);
        this.closeDropdown();
    }

    toggleItem(item: SelectComboboxItem) {
        const value = this.getValueOnItemClick(item);

        this._selectedValue$.next({
            emitEvent: true,
            value: value,
        });
    }

    closeDropdown() {
        this.isOpened = false;
    }

    openDropdown() {
        if (this.isDisabled) {
            return;
        }

        this.onTouched();

        if (!this.isOpened) {
            this.isOpened = true;
        }
    }

    onFilterTextBlur() {
        this.onTouched();
    }

    writeValue(value: any): void {
        this._selectedValue$.next({ emitEvent: false, value });
    }

    registerOnChange(onChanged: (value: any) => void): void {
        this.onChanged = onChanged || noop;
    }

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched || noop;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;

        if (isDisabled) {
            this.filterTextCtrl.disable();
            this.isOpened = false;
        } else {
            this.filterTextCtrl.enable();
        }
    }

    private getValueOnItemClick(item: SelectComboboxItem) {
        if (!this.multiple) {
            const previousValue = this._selectedValue$.value?.value;

            return !this.disableToggling && previousValue === item.value
                ? undefined
                : item.value;
        } else {
            const previousValues = this._selectedValue$.value?.value;

            if (previousValues.includes(item.value)) {
                return previousValues.filter(pv => pv !== item.value);
            } else {
                return [...previousValues, item.value];
            }
        }
    }

    private isItemSelected(selectedItem: SelectComboboxItem | SelectComboboxItem[], item: SelectComboboxItem): boolean {
        if (!this.multiple) {
            return selectedItem === item;
        } else {
            return (selectedItem as SelectComboboxItem[]).includes(item);
        }
    }
}
